import { useEffect, useRef, useState } from "react";
import { createEngine, DEFAULT_APPEARANCE, isWebGL2Available, MockupRenderer, TEMPLATE_VERSION } from "../mockup-engine";

// Live design thumbnails: Discover, My mockups and the Mockups strip show the
// user's active design on each mockup. Every listing stores a small template
// (`template_thumb`, ≤ 320 px, ~170 KB) for this; listings without one keep
// their photo.
//
// One shared worker downloads and decodes the templates, and one hidden WebGL
// canvas renders every card (browsers allow only a few WebGL contexts per
// page). Finished images are cached as object URLs.

const MAX_TEMPLATES = 12; // decoded templates kept for re-rendering (~2 MB each)
const MAX_IMAGES = 300; // rendered cards
const MAX_LOADS = 4; // parallel template downloads
const ART_SIZE = 512; // longest edge of the design uploaded for thumbnails

/** URL of a listing's thumbnail template, when it matches this engine. */
export function thumbnailTemplateUrl(listing) {
  if (!listing?.template_thumb || Number(listing.template_version) !== TEMPLATE_VERSION) return null;
  return listing.template_thumb;
}

let service = null;

function getService() {
  if (service !== null) return service;
  service = false;
  try {
    if (typeof document === "undefined" || !isWebGL2Available()) return service;
    service = createService();
  } catch (error) {
    console.warn("Design thumbnails unavailable", error);
  }
  return service;
}

function createService() {
  const canvas = document.createElement("canvas");
  const renderer = new MockupRenderer(canvas);
  const output = document.createElement("canvas");
  let engine = null;
  const templates = new Map(); // url -> Promise<template>
  const images = new Map(); // key -> Promise<objectURL>
  const smallArt = new WeakMap(); // design texture -> downscaled copy
  let currentArt = null, currentTemplate = null, loads = 0, docs = 0;
  const waiting = [];

  const touch = (map, key, value) => {
    map.delete(key);
    map.set(key, value);
  };

  const acquire = () =>
    loads < MAX_LOADS ? (loads++, Promise.resolve()) : new Promise((resolve) => waiting.push(resolve));
  const release = () => {
    const next = waiting.shift();
    if (next) next();
    else loads--;
  };

  function loadTemplate(url) {
    const cached = templates.get(url);
    if (cached) {
      touch(templates, url, cached);
      return cached;
    }
    const promise = (async () => {
      await acquire();
      const docId = `thumb${++docs}`;
      try {
        engine ||= createEngine();
        const { template } = await engine.openTemplate(docId, { previewUrl: url });
        return template;
      } finally {
        engine?.close(docId).catch(() => {});
        release();
      }
    })();
    templates.set(url, promise);
    promise.catch(() => templates.delete(url));
    while (templates.size > MAX_TEMPLATES) templates.delete(templates.keys().next().value);
    return promise;
  }

  function artFor(texture) {
    let small = smallArt.get(texture);
    if (!small) {
      const s = Math.min(1, ART_SIZE / Math.max(texture.width, texture.height));
      if (s >= 1) small = texture;
      else {
        small = document.createElement("canvas");
        small.width = Math.max(1, Math.round(texture.width * s));
        small.height = Math.max(1, Math.round(texture.height * s));
        const ctx = small.getContext("2d");
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(texture, 0, 0, small.width, small.height);
      }
      smallArt.set(texture, small);
    }
    return small;
  }

  async function draw(template, texture, placement) {
    // A lost context draws nothing; fail so the card keeps its photo instead
    // of caching a blank image.
    if (renderer.lost) throw new Error("The graphics context was lost.");
    if (currentTemplate !== template) {
      renderer.setTemplate(template);
      currentTemplate = template;
    }
    const art = artFor(texture);
    if (currentArt !== art) {
      renderer.setArtwork(art);
      currentArt = art;
    }
    renderer.draw({ placement, appearance: DEFAULT_APPEARANCE });
    // The WebGL frame is only valid until the next task: copy it right away.
    output.width = template.width;
    output.height = template.height;
    output.getContext("2d").drawImage(canvas, 0, 0);
    const blob = await new Promise((resolve) => output.toBlob(resolve, "image/webp", 0.86));
    if (!blob) throw new Error("Could not encode the thumbnail.");
    return URL.createObjectURL(blob);
  }

  return {
    /**
     * Renders `design` ({ key, texture, placementFor(w, h) }) on the
     * template at `url`. Resolves to an object URL.
     */
    render(url, design) {
      const key = `${url}|${design.key}`;
      const cached = images.get(key);
      if (cached) {
        touch(images, key, cached);
        return cached;
      }
      const promise = loadTemplate(url).then((template) =>
        draw(template, design.texture, design.placementFor(template.designWidth, template.designHeight)),
      );
      images.set(key, promise);
      promise.catch(() => images.delete(key));
      while (images.size > MAX_IMAGES) {
        const oldest = images.keys().next().value;
        // Images already shown stay decoded in their <img>; revoking frees the blob.
        images.get(oldest).then((u) => URL.revokeObjectURL(u), () => {});
        images.delete(oldest);
      }
      return promise;
    },
  };
}

/**
 * The listing's mockup rendered with `design`, once the card is on screen.
 * Returns [ref for the card element, image URL or null]. Keeps the previous
 * image while a new design or placement renders, so cards don't flash back to
 * the photo. `delay` debounces rapid changes (e.g. dragging the design).
 */
export function useDesignThumbnail(listing, design, { delay = 0 } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [src, setSrc] = useState(null);
  const url = design ? thumbnailTemplateUrl(listing) : null;
  const key = url && design ? `${url}|${design.key}` : null;

  useEffect(() => {
    const el = ref.current;
    if (!el || visible || !url) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && setVisible(true), { rootMargin: "200px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [url, visible]);

  const designRef = useRef(design);
  designRef.current = design;
  useEffect(() => {
    if (!key) {
      setSrc(null);
      return undefined;
    }
    if (!visible) return undefined;
    const svc = getService();
    if (!svc) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      svc.render(url, designRef.current).then(
        (u) => !cancelled && setSrc(u),
        (error) => {
          if (!cancelled) console.warn("Design thumbnail failed", error);
        },
      );
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [key, url, visible, delay]);

  return [ref, key ? src : null];
}
